import React, { useEffect, useMemo, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { SitesTable } from './SitesTable';

import { fetchSitesMonitoring, SitesSearchDto } from '../../app/Sites/siteApi';
import { DevicesTable } from './DevicesTable';
import { Link, useNavigate } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Camera,
  Building2,
  Shield,
  Table2Icon,
  TableIcon,
  Building2Icon,
  CameraIcon,
  AlertTriangleIcon,
  AlertCircleIcon,
  LayoutGridIcon,
  MoveIcon,
  Network,
} from 'lucide-react';
import { SitesTableFilters } from './SitesTableFilters';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import CustomerSelectDialog from './AddDevice/CustomerSelectDialog';
import { useSearchDevices } from '@/hooks/connectNewDevice';
import Header from '@/components/Header';

interface Site {
  id: string;
  name: string;
  client: string;
  location: string;
  status: 'online' | 'warning' | 'offline';
  issues: number;
  companyId: string;
  companyName?: string;
}

interface Device {
  id: string;
  deviceId: string;
  model: string;
  site: string;
  customer: string;
  status: 'online' | 'warning' | 'offline';
  deviceType: string;
  issues: Issue[];
  lastChecked: string;
  description: string;
}

interface Issue {
  id: number;
  device: string;
  model: string;
  site: string;
  group: string;
  message: string;
  severity: 'high' | 'medium' | 'low';
}

const Devices: React.FC = () => {
  const navigate = useNavigate();
  const [issueGrouping, setIssueGrouping] = React.useState('type');

  const [loadingSites, setLoadingSites] = useState(true);
  const [errorSites, setErrorSites] = useState<string | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [siteViewMode, setSiteViewMode] = useState<'table' | 'cards'>('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [customSelectDialogOpen, setCustomSelectDialogOpen] = useState(false);

  const transformSiteData = (siteData: SitesSearchDto): Site => ({
    id: siteData.id || '',
    name: siteData.name || '',
    client: siteData.companyName || '',
    location: siteData.address1 || '',
    status: 'online',
    issues: siteData.issues || 0,
    companyId: siteData.companyId || '',
    companyName: siteData.companyName,
  });

  useEffect(() => {
    const fetchSites = async () => {
      setLoadingSites(true);
      try {
        const data = await fetchSitesMonitoring();
        if (Array.isArray(data)) {
          setSites(data.map(transformSiteData));
        } else {
          setSites([]);
          setErrorSites('Invalid data format received');
        }
      } catch (err) {
        setErrorSites('Failed to fetch sites');
        console.error('Error fetching sites:', err);
      } finally {
        setLoadingSites(false);
      }
    };

    fetchSites();
  }, []);

  const { data: nvrs } = useSearchDevices({ searchQuery: { query: '' } });

  const devices: Device[] = useMemo(
    () =>
      nvrs?.devices?.map(
        (device): Device => ({
          lastChecked: 'Never',
          deviceType: 'Nvr',
          deviceId: device.nvr.externalId,
          customer: 'Zains Bakery',
          status: 'online',
          issues: [],
          model: 'HiK Vision',
          site: 'Zains Bakery',
          id: device.nvr.id,
          description: device.nvr.description,
        })
      ) ?? [],
    [nvrs]
  );
  // Add these mock issues
  const issuesByType = [
    {
      type: 'Cameras',
      issues: [
        {
          id: 1,
          device: 'CCTV-001',
          model: 'Hikvision DS-2CD2385G1',
          site: 'Warehouse Site 1',
          group: 'Perimeter Cameras',
          message: 'Camera offline - No video feed',
          severity: 'high',
        },
        {
          id: 2,
          device: 'CCTV-003',
          model: 'Bosch NDC-225-P',
          site: 'Distribution Center',
          group: 'Indoor Cameras',
          message: 'Sensor performance degraded - please check',
          severity: 'medium',
        },
      ],
    },
    {
      type: 'Motion Grid Change Requests',
      issues: [
        {
          id: 3,
          device: 'CAM-056',
          model: 'Axis P3245-LVE',
          site: 'Warehouse Site 1',
          group: 'Motion Detection',
          message: 'Motion zones need recalibration',
          severity: 'medium',
        },
        {
          id: 4,
          device: 'CAM-089',
          model: 'Hikvision DS-2CD2T47G1',
          site: 'Distribution Center',
          group: 'Motion Detection',
          message: 'False motion alerts detected',
          severity: 'high',
        },
      ],
    },
    {
      type: 'Devices Moved',
      issues: [
        {
          id: 5,
          device: 'CAM-023',
          site: 'Distribution Center',
          group: 'Indoor Cameras',
          model: 'Axis P3265-LV',
          message: 'Camera position changed - Needs realignment',
          severity: 'medium',
        },
        {
          id: 6,
          device: 'CAM-045',
          site: 'Distribution Center',
          group: 'Perimeter Cameras',
          model: 'Hikvision DS-2CD2686G2',
          message: 'Device relocated without authorization',
          severity: 'high',
        },
        {
          id: 7,
          device: 'CAM-012',
          site: 'Warehouse Site 2',
          group: 'Loading Dock Cameras',
          model: 'Bosch NBE-4502-AL',
          message: 'Camera angle adjusted - Coverage gap detected',
          severity: 'medium',
        },
      ],
    },
  ];

  const issuesBySite = [
    {
      site: 'Warehouse Site 1',
      address: '41 Piritahi Road, Auckland 1010',
      issues: [
        {
          id: 1,
          device: 'CCTV-001',
          model: 'Hikvision DS-2CD2385G1',
          site: 'Warehouse Site 1',
          group: 'Perimeter Cameras',
          message: 'Device offline for 2 hours',
          severity: 'high',
        },
        {
          id: 3,
          device: 'NVR-002',
          model: 'Hikvision DS-9664NI-I8',
          site: 'Warehouse Site 1',
          group: 'Recording Systems',
          message: 'Storage capacity critical (95%)',
          severity: 'high',
        },
      ],
    },
    {
      site: 'Distribution Center',
      address: '41 Piritahi Road, Auckland 1010',
      issues: [
        {
          id: 2,
          device: 'CCTV-003',
          site: 'Distribution Center',
          group: 'Indoor Cameras',
          model: 'banana',
          message: 'Intermittent connection',
          severity: 'medium',
        },
        {
          id: 4,
          device: 'ACC-001',
          site: 'Distribution Center',
          group: 'Access Control',
          model: 'banana',
          message: 'Battery backup failing',
          severity: 'medium',
        },
        {
          id: 5,
          device: 'CCTV-002',
          site: 'Distribution Center',
          group: 'Perimeter Cameras',
          model: 'banana',
          message: 'Firmware update required',
          severity: 'low',
        },
      ],
    },
    {
      site: 'Warehouse Site 2',
      issues: [
        {
          id: 2,
          device: 'CCTV-003',
          site: 'Distribution Center',
          group: 'Indoor Cameras',
          model: 'banana',
          message: 'Intermittent connection',
          severity: 'medium',
        },
        {
          id: 4,
          device: 'ACC-001',
          site: 'Distribution Center',
          group: 'Access Control',
          model: 'banana',
          message: 'Battery backup failing',
          severity: 'medium',
        },
        {
          id: 5,
          device: 'CCTV-002',
          site: 'Distribution Center',
          group: 'Perimeter Cameras',
          model: 'banana',
          message: 'Firmware update required',
          severity: 'low',
        },
      ],
    },
  ];

  const getTotalIssueCount = () => {
    if (issueGrouping === 'type') {
      return issuesByType.reduce((total, group) => total + group.issues.length, 0);
    }
    return issuesBySite.reduce((total, group) => total + group.issues.length, 0);
  };

  const filteredSites = sites.filter((site) => {
    const matchesSearch =
      site.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      site.client.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter.length === 0 || statusFilter.includes(site.status);

    return matchesSearch && matchesStatus;
  });

  const renderSitesContent = () => {
    if (loadingSites) {
      return <div className="p-4 text-center">Loading sites...</div>;
    }

    if (errorSites) {
      return <div className="p-4 text-center text-red-500">{errorSites}</div>;
    }

    if (filteredSites.length === 0) {
      return (
        <div className="col-span-full py-8 text-center text-gray-500">
          No sites match your search criteria
        </div>
      );
    }

    return siteViewMode === 'table' ? (
      <SitesTable sites={filteredSites} />
    ) : (
      <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredSites.map((site) => (
          <div key={site.id} className="flex-1 rounded-lg border shadow-sm">
            <div className="flex items-center gap-4 border-b p-6">
              <div
                className={cn(
                  'h-4 w-4 rounded-full',
                  site.status === 'online' && 'bg-green-400',
                  site.status === 'warning' && 'bg-yellow-400',
                  site.status === 'offline' && 'bg-red-400'
                )}
              />
              <div>
                <Link to={`/Customer/${site.companyId}/project/${site.id}`}>
                  <h2 className="font-semibold text-lime hover:underline">{site.name}</h2>
                </Link>
                <p className="text-gray-400">{site.client}</p>
                <p className="text-sm text-gray-500">Pukekohe, Auckland, New Zealand</p>
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2">
                <span className="text-gray-400">{site.issues} issues</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="flex justify-between border-b p-5">
        <div className="flex items-center gap-2">
          <Network />
          <h1 className="text-2xl font-semibold">Network Health</h1>
        </div>
      </div>
      <section className="m-6 text-white max-2xl:m-0">
        <CustomerSelectDialog
          isDialogOpen={customSelectDialogOpen}
          setIsDialogOpen={setCustomSelectDialogOpen}
        />

        <div className="col-span-3 rounded-lg border">
          <div className="">
            <div className="flex items-center justify-between border-b p-6">
              <div className="flex items-center gap-4">
                <div className="h-4 w-4 rounded-full bg-yellow-500" />
                <h2 className="text-xl font-semibold">{getTotalIssueCount()} issues to resolve</h2>
              </div>
              <Select defaultValue="type" onValueChange={setIssueGrouping}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Group by..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="type">Group by Type</SelectItem>
                  <SelectItem value="site">Group by Site</SelectItem>
                  <SelectItem value="customer">Group by Customer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <section className="grid grid-cols-3 gap-4 border-b">
              <div className="flex flex-1 items-center gap-4 border-r p-6">
                <CameraIcon className="h-5 w-5" />
                <div>
                  <h2 className="font-semibold">Camera Issues</h2>
                  <p>2</p>
                </div>
              </div>
              <div className="flex flex-1 items-center gap-4 border-r p-6">
                <LayoutGridIcon className="h-5 w-5" />
                <div>
                  <h2 className="font-semibold">Motion Grid Changes</h2>
                  <p>2</p>
                </div>
              </div>
              <div className="flex flex-1 items-center gap-4 p-6">
                <MoveIcon className="h-5 w-5" />
                <div>
                  <h2 className="font-semibold">Devices Moved</h2>
                  <p>3</p>
                </div>
              </div>
            </section>

            <div className="">
              {issueGrouping === 'type'
                ? // Render issues grouped by type
                  issuesByType.map((group) => (
                    <div key={group.type} className="border-b p-6">
                      <div className="flex items-center justify-start gap-2 border-b pb-4">
                        <h3 className="text-lg font-semibold">
                          {group.type} - {group.issues.length} issues
                        </h3>
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[180px]">Device</TableHead>
                            <TableHead className="w-[300px]">Status</TableHead>
                            <TableHead className="w-[250px]">Customer & Location</TableHead>
                            <TableHead className="w-[250px]">Device Details</TableHead>
                            <TableHead className="w-[100px] text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.issues.map((issue) => (
                            <TableRow key={issue.id}>
                              <TableCell>
                                <p className="font-semibold text-lime underline">{issue.device}</p>
                              </TableCell>

                              <TableCell>
                                <div className="flex items-center gap-3">
                                  <div
                                    className={cn(
                                      'h-2 w-2 rounded-full',
                                      issue.severity === 'high'
                                        ? 'bg-red-500'
                                        : issue.severity === 'medium'
                                          ? 'bg-yellow-500'
                                          : 'bg-blue-500'
                                    )}
                                  />
                                  <div>
                                    <p className="text-xs text-white">{issue.message}</p>
                                    <div className="mb-1 flex items-center gap-1 text-xs text-gray-400">
                                      <span>5 minutes ago</span>
                                    </div>
                                  </div>
                                </div>
                              </TableCell>

                              <TableCell>
                                <div className="space-y-2">
                                  <div className="flex items-center gap-3">
                                    <Building2 className="h-4 w-4 text-gray-400" />
                                    <div>
                                      <p className="text-xs font-medium text-white">
                                        Fictional Customer
                                      </p>
                                      <p className="text-xs text-gray-400">
                                        {issue.site} - {issue.group}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </TableCell>

                              <TableCell>
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <Camera className="h-4 w-4 text-gray-400" />
                                    <div>
                                      <p className="text-xs text-white">#1231232</p>
                                      <p className="text-xs text-gray-400">{issue.model}</p>
                                    </div>
                                  </div>
                                </div>
                              </TableCell>

                              <TableCell className="text-right">
                                <Button variant="outline" size="sm">
                                  Resolve
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ))
                : // Render issues grouped by site
                  issuesBySite.map((group) => (
                    <div key={group.site} className="border-b p-6">
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-white">
                          {group.site} -{' '}
                          <span className="font-normal text-gray-400">
                            {group.issues.length} issues
                          </span>
                        </h3>
                        <h3 className="font-medium text-gray-400">{group.address}</h3>
                      </div>

                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[180px]">Device</TableHead>
                            <TableHead className="w-[300px]">Status</TableHead>
                            <TableHead className="w-[250px]">Customer & Location</TableHead>
                            <TableHead className="w-[250px]">Device Details</TableHead>
                            <TableHead className="w-[100px] text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.issues.map((issue) => (
                            <TableRow key={issue.id}>
                              <TableCell>
                                <p className="font-semibold text-lime underline">{issue.device}</p>
                              </TableCell>

                              <TableCell>
                                <div className="flex items-center gap-3">
                                  <div
                                    className={cn(
                                      'h-2 w-2 rounded-full',
                                      issue.severity === 'high'
                                        ? 'bg-red-500'
                                        : issue.severity === 'medium'
                                          ? 'bg-yellow-500'
                                          : 'bg-blue-500'
                                    )}
                                  />
                                  <div>
                                    <p className="text-xs text-white">{issue.message}</p>
                                    <div className="mb-1 flex items-center gap-1 text-xs text-gray-400">
                                      <span>5 minutes ago</span>
                                    </div>
                                  </div>
                                </div>
                              </TableCell>

                              <TableCell>
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <Shield className="h-4 w-4 text-gray-400" />
                                    <span className="text-xs text-gray-300">
                                      Fictional Customer
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Building2 className="h-4 w-4 text-gray-400" />
                                    <span className="text-xs text-gray-300">
                                      {issue.site}, {issue.group}
                                    </span>
                                  </div>
                                </div>
                              </TableCell>

                              <TableCell>
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <Camera className="h-4 w-4 text-gray-400" />
                                    <span className="text-xs text-gray-300">{issue.model}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Shield className="h-4 w-4 text-gray-400" />
                                    <span className="text-xs text-gray-300"># 1231232</span>
                                  </div>
                                </div>
                              </TableCell>

                              <TableCell className="text-right">
                                <Button variant="outline" size="sm">
                                  Resolve
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Devices;
